import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { Users, Copy, Check, Gift, MessageCircle, Twitter, Facebook, Send } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { API } from '../utils/helpers';

const ReferralWidget = () => {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [showApply, setShowApply] = useState(false);
  const [refInput, setRefInput] = useState('');
  const [applying, setApplying] = useState(false);

  useEffect(() => {
    fetch(`${API}/user/referral`, { credentials: 'include' })
      .then(res => res.json())
      .then(setData)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const copyLink = () => {
    if (data?.referral_link) {
      navigator.clipboard.writeText(data.referral_link);
      setCopied(true);
      toast.success('Enlace copiado');
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const shareText = "🔮 Descubre tu destino con Madame Blavatsky - Tarot, Astrología y Numerología. ¡Únete con mi enlace y recibe 5 créditos gratis!";

  const shareOnWhatsApp = () => {
    const url = `https://wa.me/?text=${encodeURIComponent(shareText + '\n\n' + data.referral_link)}`;
    window.open(url, '_blank');
  };

  const shareOnTwitter = () => {
    const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(data.referral_link)}`;
    window.open(url, '_blank');
  };

  const shareOnFacebook = () => {
    const url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(data.referral_link)}&quote=${encodeURIComponent(shareText)}`;
    window.open(url, '_blank');
  };

  const shareOnTelegram = () => {
    const url = `https://t.me/share/url?url=${encodeURIComponent(data.referral_link)}&text=${encodeURIComponent(shareText)}`;
    window.open(url, '_blank');
  };

  const applyCode = async () => {
    if (!refInput.trim()) return;
    setApplying(true);
    try {
      const res = await fetch(`${API}/referral/apply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ referral_code: refInput.trim() })
      });
      const result = await res.json();
      if (res.ok) {
        toast.success(result.message);
        setShowApply(false);
        setRefInput('');
      } else {
        toast.error(result.detail);
      }
    } catch (e) { toast.error('Error al aplicar codigo'); }
    setApplying(false);
  };

  if (loading) return <div className="glass-card p-6 mb-8 animate-pulse h-24" />;
  if (!data) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
      className="glass-card p-6 mb-8 border border-gold-300/10"
      data-testid="referral-widget"
    >
      <h2 className="text-lg font-bold gold-text mb-4 flex items-center gap-2">
        <Users className="w-5 h-5" /> Invita Amigos
      </h2>

      <div className="flex flex-col sm:flex-row gap-4 mb-4">
        {/* Your referral link */}
        <div className="flex-1 p-4 rounded-xl bg-white/5 border border-gold-300/8">
          <p className="text-xs text-[var(--text-muted)] mb-2">Tu codigo de referido</p>
          <p className="text-lg font-bold text-purple-300 font-mono mb-2" data-testid="referral-code">{data.referral_code}</p>
          
          {/* Short link display */}
          <div className="flex gap-2 mb-3">
            <input
              type="text"
              value={data.referral_link}
              readOnly
              className="mystic-input flex-1 text-xs py-1 font-mono"
              data-testid="referral-link"
            />
            <button
              onClick={copyLink}
              className={`px-3 py-1 rounded-lg text-xs transition-all ${
                copied ? 'bg-green-600 text-white' : 'bg-purple-400/10 text-purple-300 hover:bg-purple-600/30'
              }`}
              data-testid="copy-referral-btn"
            >
              {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
            </button>
          </div>

          {/* Share buttons */}
          <p className="text-xs text-[var(--text-muted)] mb-2">Compartir en redes</p>
          <div className="flex gap-2">
            <button
              onClick={shareOnWhatsApp}
              className="flex-1 flex items-center justify-center gap-1 px-3 py-2 rounded-lg bg-green-600/20 text-green-400 hover:bg-green-600/30 transition-all text-xs"
              data-testid="share-whatsapp"
            >
              <MessageCircle className="w-4 h-4" />
              <span className="hidden sm:inline">WhatsApp</span>
            </button>
            <button
              onClick={shareOnTwitter}
              className="flex-1 flex items-center justify-center gap-1 px-3 py-2 rounded-lg bg-blue-500/20 text-blue-400 hover:bg-blue-500/30 transition-all text-xs"
              data-testid="share-twitter"
            >
              <Twitter className="w-4 h-4" />
              <span className="hidden sm:inline">Twitter</span>
            </button>
            <button
              onClick={shareOnFacebook}
              className="flex-1 flex items-center justify-center gap-1 px-3 py-2 rounded-lg bg-blue-600/20 text-blue-300 hover:bg-blue-600/30 transition-all text-xs"
              data-testid="share-facebook"
            >
              <Facebook className="w-4 h-4" />
              <span className="hidden sm:inline">Facebook</span>
            </button>
            <button
              onClick={shareOnTelegram}
              className="flex-1 flex items-center justify-center gap-1 px-3 py-2 rounded-lg bg-sky-500/20 text-sky-400 hover:bg-sky-500/30 transition-all text-xs"
              data-testid="share-telegram"
            >
              <Send className="w-4 h-4" />
              <span className="hidden sm:inline">Telegram</span>
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="flex flex-row sm:flex-col gap-4 sm:gap-2 p-4 rounded-xl bg-white/5 border border-gold-300/8 text-center sm:min-w-[120px]">
          <div>
            <p className="text-2xl font-bold text-purple-400">{data.referrals_count}</p>
            <p className="text-xs text-[var(--text-muted)]">Referidos</p>
          </div>
          <div>
            <p className="text-2xl font-bold text-green-400">+{data.credits_earned}</p>
            <p className="text-xs text-[var(--text-muted)]">Creditos ganados</p>
          </div>
        </div>
      </div>

      {/* Rewards info */}
      <div className="flex items-center gap-3 p-3 rounded-lg bg-yellow-500/5 border border-yellow-500/10 mb-3">
        <Gift className="w-5 h-5 text-yellow-400 shrink-0" />
        <p className="text-xs text-[var(--text-muted)]">
          <span className="text-yellow-400 font-medium">+5 creditos</span> para ti y <span className="text-yellow-400 font-medium">+5 creditos</span> para tu amigo al registrarse con tu enlace
        </p>
      </div>

      {/* Apply referral code */}
      {!showApply ? (
        <button
          onClick={() => setShowApply(true)}
          className="text-xs text-purple-400 underline"
          data-testid="show-apply-referral"
        >
          Tengo un codigo de referido
        </button>
      ) : (
        <div className="flex gap-2">
          <input
            type="text"
            value={refInput}
            onChange={e => setRefInput(e.target.value.toUpperCase())}
            placeholder="Codigo de referido..."
            className="mystic-input flex-1 text-sm"
            data-testid="referral-input"
          />
          <button
            onClick={applyCode}
            disabled={applying || !refInput.trim()}
            className="btn-gold text-sm"
            data-testid="apply-referral-btn"
          >
            {applying ? 'Aplicando...' : 'Aplicar'}
          </button>
          <button onClick={() => setShowApply(false)} className="btn-ghost text-sm">X</button>
        </div>
      )}
    </motion.div>
  );
};

export default ReferralWidget;
